import React, { useState } from 'react';
import { Mail, Phone, ArrowLeft, CreditCard, Loader2, Zap, Lock, Eye, EyeOff, ExternalLink, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getBackendUrl } from '../utils/apiUrl';

const TenantSuspendedPage = ({ tenantName, subdomain, reason = 'trial_expired', source = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const isShopifyManaged = source === 'shopify_app_store';

  const handlePayAndContinue = async () => {
    const token = localStorage.getItem('plateos_token') || localStorage.getItem('plateos_token');

    if (!token) {
      setShowLogin(true);
      return;
    }

    await initiateCheckout(token);
  };

  const handleLoginAndPay = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const backendUrl = getBackendUrl();
      const loginResp = await fetch(`${backendUrl}/api/auth/tenant-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          subdomain: subdomain || 'plateos-super-admin',
          login_type: 'admin',
        }),
      });

      if (!loginResp.ok) {
        const errData = await loginResp.json().catch(() => ({}));
        throw new Error(errData.detail || 'Invalid email or password');
      }

      const loginData = await loginResp.json();
      const token = loginData.access_token;

      localStorage.setItem('plateos_token', token);
      localStorage.setItem('token', token);
      if (loginData.user) {
        localStorage.setItem('plateos_user', JSON.stringify(loginData.user));
        localStorage.setItem('user', JSON.stringify(loginData.user));
        if (loginData.user.tenant_id) {
          localStorage.setItem('tenant_id', loginData.user.tenant_id);
        }
      }

      await initiateCheckout(token);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const initiateCheckout = async (token) => {
    setLoading(true);
    setError(null);

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/billing/create-reactivation-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('plateos_token');
        setShowLogin(true);
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error (${response.status})`);
      }

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received. Please contact support.');
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate payment. Please contact support.');
      setLoading(false);
    }
  };

  const reasonMessages = {
    trial_expired: { title: 'Trial Period Ended', description: 'Your trial period has ended. Upgrade to continue using PlateOS.' },
    payment_failed: { title: 'Payment Issue', description: 'We were unable to process your payment. Please update your payment method.' },
    subscription_expired: { title: 'Subscription Expired', description: 'Your subscription has expired. Renew now to restore access.' },
    admin_suspended: { title: 'Account Suspended', description: 'Your account has been suspended. Please pay to reactivate or contact support.' },
    over_customer_cap: { title: 'Upgrade to Business to continue', description: "Your business has grown past its plan's active-customer limit. Upgrade to Business ($199/mo) for unlimited customers and keep everything running." },
    shopify_app_uninstalled: { title: 'App Uninstalled', description: 'PlateOS was removed from your Shopify store. Reinstall to restore access.' },
  };

  const currentReason = reasonMessages[isShopifyManaged && reason === 'admin_suspended' ? 'shopify_app_uninstalled' : reason] || reasonMessages.admin_suspended;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-8 py-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
              <Lock className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="suspended-title">{currentReason.title}</h1>
            <p className="text-amber-100">{tenantName || subdomain || 'Your account'}</p>
          </div>

          <div className="px-8 py-8">
            <p className="text-slate-600 text-center mb-6">{currentReason.description}</p>

            {/* Data safe reminder */}
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-4 mb-6 border border-teal-100">
              <p className="text-sm font-medium text-teal-800 mb-2">Your data is safe! Reactivate to access:</p>
              <ul className="text-xs text-teal-700 space-y-1">
                <li className="flex items-center gap-1.5"><span className="text-teal-500 font-bold">&#10003;</span> All your customers and orders</li>
                <li className="flex items-center gap-1.5"><span className="text-teal-500 font-bold">&#10003;</span> Meal plans and configurations</li>
                <li className="flex items-center gap-1.5"><span className="text-teal-500 font-bold">&#10003;</span> Analytics and reports</li>
              </ul>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2" data-testid="suspended-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Shopify-managed: show reinstall CTA */}
            {isShopifyManaged ? (
              <div className="space-y-3 mb-6">
                <a
                  href="https://apps.shopify.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-14 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg font-semibold shadow-lg rounded-lg transition-colors"
                  data-testid="reinstall-shopify-btn"
                >
                  <ExternalLink className="h-5 w-5" />
                  Reinstall PlateOS from Shopify
                </a>
                <p className="text-xs text-slate-500 text-center">
                  Your Shopify billing is managed through the Shopify App Store.
                  Reinstall the app to restore your account and all data.
                </p>
              </div>
            ) : (
              <>
                {/* Inline login form (shown when no token) */}
                {showLogin ? (
                  <form onSubmit={handleLoginAndPay} className="space-y-4 mb-6">
                    <p className="text-sm font-medium text-slate-700 text-center">Log in to continue to payment</p>
                    <div className="space-y-2">
                      <Label htmlFor="reactivate-email" className="text-slate-600 text-sm">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="reactivate-email"
                          type="email"
                          placeholder="admin@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          data-testid="reactivate-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reactivate-password" className="text-slate-600 text-sm">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="reactivate-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          data-testid="reactivate-password"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold"
                      data-testid="login-and-pay-btn"
                    >
                      {loading ? (
                        <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><CreditCard className="h-5 w-5 mr-2" />Log In &amp; Pay</>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-3 mb-6">
                    <Button
                      onClick={handlePayAndContinue}
                      disabled={loading}
                      className="w-full h-14 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-lg font-semibold shadow-lg"
                      data-testid="pay-continue-btn"
                    >
                      {loading ? (
                        <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><CreditCard className="h-5 w-5 mr-2" />Pay &amp; Continue<Zap className="h-5 w-5 ml-2" /></>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">Secure payment powered by Stripe</p>
                  </div>
                )}
              </>
            )}

            {/* Contact Support */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 text-center">Need Help? Contact Support</h3>
              <div className="space-y-3">
                <a
                  href="mailto:support@plateos.io?subject=Account Reactivation Request"
                  className="flex items-center justify-center gap-3 w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                  data-testid="support-email-link"
                >
                  <Mail className="h-5 w-5" />
                  <span className="font-medium">support@plateos.io</span>
                </a>
                <a
                  href="tel:+1-800-PLATEOS"
                  className="flex items-center justify-center gap-3 w-full py-3 px-4 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-lg transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  <span>Call Sales Team</span>
                </a>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a href="https://plateos.io" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Back to PlateOS.io
              </a>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-400 text-sm mt-6">
          &copy; {new Date().getFullYear()} PlateOS. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default TenantSuspendedPage;
